'use client'

/**
 * The task slice's write spec: what a task draft is, and the three writes over it.
 *
 * The mechanics are `document/operations/`' (#289); this is the task vocabulary over them
 * (ADR 0012:30-36). It names nothing from `legacy/`: `TaskInsert` and `TaskUpdate` were Supabase
 * payload types, and their snake_case reached the row's `onSave` payload as `due_date` and
 * `checklist_items` on an Automerge entry.
 */

import { useMutation } from '@tanstack/react-query'

import {
  useDocumentOps,
  useDocumentView,
  type EntryChange,
  type MetadataPatch,
} from '@/document'
import type {
  ChecklistItem,
  StoredTaskPriority,
  StoredTaskStatus,
} from './meta'

/**
 * What a form edits and these three writes accept.
 *
 * Camel-cased throughout: `due_date` is `dueDate`, `checklist_items` is `checklist`, `project_id`
 * is `projectId`. **Three members of `TaskInsert` are dropped rather than renamed** — `category`,
 * `is_recurring` and `recurrence`. The capture form passed all three and `useCreateTask` read none
 * of them, so they never reached a document; carrying them into the slice would ship an interface
 * over fields nothing writes.
 */
export interface TaskDraft {
  title: string
  description: string | null
  status: StoredTaskStatus
  priority: StoredTaskPriority
  important: boolean
  dueDate: string | null
  checklist: ChecklistItem[] | null
  projectId: string | null
  topicIds: string[]
  peopleIds: string[]
}

export function useCreateTask() {
  const doc = useDocumentView()
  const ops = useDocumentOps()

  return useMutation({
    mutationFn: async (data: Partial<TaskDraft> & { title: string }) => {
      if (!doc) {
        throw new Error('Document not ready')
      }

      const id = ops.createEntry({
        type: 'task',
        title: data.title,
        content: data.description || '',
        metadata: {
          status: data.status,
          important: data.important ?? false,
          dueDate: data.dueDate,
          checklist: data.checklist || null,
        },
        projectId: data.projectId || null,
        topicIds: data.topicIds || [],
        peopleIds: data.peopleIds,
      })

      return { id }
    },
  })
}

export function useUpdateTask() {
  const doc = useDocumentView()
  const ops = useDocumentOps()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: Partial<TaskDraft>
    }) => {
      if (!doc) {
        throw new Error('Document not ready')
      }

      const metadata: MetadataPatch = {}
      if (data.status !== undefined) {
        metadata.status = data.status
        // Stamp the completion time when the task is marked done, clear it when it is un-done.
        // `undefined` in a patch is a clear, and clearing a key that was never set is a no-op.
        metadata.completedAt =
          data.status === 'done' ? new Date().toISOString() : undefined
      }
      if (data.priority !== undefined) metadata.priority = data.priority
      if (data.important !== undefined) metadata.important = data.important
      if (data.dueDate !== undefined) metadata.dueDate = data.dueDate
      if (data.checklist !== undefined) {
        metadata.checklist = data.checklist ?? undefined
      }

      const change: EntryChange = { metadata }
      if (data.title !== undefined) change.title = data.title
      if (data.description !== undefined) {
        change.content = data.description || ''
      }
      if (data.projectId !== undefined) change.projectId = data.projectId
      if (data.topicIds !== undefined) change.topicIds = data.topicIds
      if (data.peopleIds !== undefined) change.peopleIds = data.peopleIds

      if (!ops.updateEntry(id, change)) {
        throw new Error(`Task ${id} not found`)
      }

      return doc.entries[id]
    },
  })
}

/**
 * Let a task go from a review, which is not the same write as deleting it.
 *
 * `useDeleteTask` calls `ops.archiveEntry`, and that operation deliberately does **not** stamp
 * `updatedAt` — `document/operations/__tests__` pins it. The stale-task review selects on
 * `updatedAt`, so dismissing a task there has to move it: an archive that left the timestamp alone
 * would be invisible to the rule that surfaced the task in the first place. The card wrote this
 * through `ops.updateEntry` directly until #406; the write is task vocabulary, so it lives here.
 */
export function useDismissTask() {
  const doc = useDocumentView()
  const ops = useDocumentOps()

  return useMutation({
    mutationFn: async (id: string) => {
      if (!doc) {
        throw new Error('Document not ready')
      }

      if (!ops.updateEntry(id, { archivedAt: new Date().toISOString() })) {
        throw new Error(`Task ${id} not found`)
      }
    },
  })
}

export function useDeleteTask() {
  const doc = useDocumentView()
  const ops = useDocumentOps()

  return useMutation({
    mutationFn: async (id: string) => {
      if (!doc) {
        throw new Error('Document not ready')
      }

      if (!ops.archiveEntry(id)) {
        throw new Error(`Task ${id} not found`)
      }
    },
  })
}

/**
 * Mark a task done, or un-done.
 *
 * `TaskRow` called an `onComplete` prop for this, which the feed and both task views each wired to
 * their own `updateTask.mutate`. ADR 0012 puts a write in the slice's write spec, and
 * `RowProps` has no room for a per-kind callback — so the three wirings become one.
 */
export function useCompleteTask() {
  const updateTask = useUpdateTask()

  return (id: string, done: boolean) => {
    updateTask.mutate({ id, data: { status: done ? 'done' : 'todo' } })
  }
}

/**
 * Check or uncheck one item on a task's checklist.
 *
 * The toggle is `toggleChecklistItem`'s; what this adds is the write. The optimistic state the feed
 * kept alongside it is gone: `ops.updateEntry` applies into the document synchronously, so the next
 * render already sees the new value.
 */
export function useToggleChecklistItem() {
  const updateTask = useUpdateTask()

  return (id: string, checklist: ChecklistItem[]) => {
    updateTask.mutate({ id, data: { checklist } })
  }
}
